import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Search } from "lucide-react";
import { api, API_BASE, TOKEN_KEY, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 25;

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = typeof iso === "string" ? new Date(iso) : iso;
        return d.toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return String(iso); }
}

export default function AdminWaitlistPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [stateSlug, setStateSlug] = useState("");
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [knownSlugs, setKnownSlugs] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
            if (stateSlug) params.state_slug = stateSlug;
            if (debouncedQ.trim()) params.q = debouncedQ.trim();
            const res = await api.get("/admin/waitlist", { params });
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
            // Track slugs we see for the picker.
            setKnownSlugs((prev) => {
                const s = new Set(prev);
                (res.data.items || []).forEach((r) => r.state_slug && s.add(r.state_slug));
                return Array.from(s).sort();
            });
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [page, stateSlug, debouncedQ]);

    // Debounce free-text search.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(t);
    }, [q]);

    // Reset to page 0 when filters change.
    useEffect(() => { setPage(0); }, [stateSlug, debouncedQ]);

    useEffect(() => { load(); }, [load]);

    // First-load: fetch an unfiltered snapshot to prime the slug picker so
    // admins see all options, not just what's on page 1.
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/admin/waitlist", { params: { limit: 1000 } });
                const s = new Set();
                (res.data.items || []).forEach((r) => r.state_slug && s.add(r.state_slug));
                setKnownSlugs(Array.from(s).sort());
            } catch { /* ignore — picker will still work from paged results */ }
        })();
    }, []);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

    const onExport = async () => {
        try {
            const params = new URLSearchParams();
            if (stateSlug) params.set("state_slug", stateSlug);
            if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
            const url = `${API_BASE}/admin/waitlist/export.csv${params.toString() ? `?${params}` : ""}`;
            const token = localStorage.getItem(TOKEN_KEY);
            const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            const today = new Date().toISOString().slice(0, 10);
            dl.download = `waitlist-${today}.csv`;
            document.body.appendChild(dl);
            dl.click();
            dl.remove();
            URL.revokeObjectURL(dl.href);
            toast.success("Waitlist CSV downloaded");
        } catch (e) {
            toast.error(e.message || "Export failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-waitlist-page">
            <header>
                <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Growth</div>
                <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)] sm:text-4xl">
                    Waitlist
                </h1>
                <p className="mt-1 font-body text-sm text-[var(--text-muted)]">
                    Leads captured from unsupported regions and coming-soon categories.
                </p>
            </header>

            {/* Filter row */}
            <div className="card-soft flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex flex-1 flex-col gap-1">
                    <label htmlFor="wl-state" className="font-heading text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                        Source / State
                    </label>
                    <select
                        id="wl-state"
                        value={stateSlug}
                        onChange={(e) => setStateSlug(e.target.value)}
                        data-testid="admin-waitlist-filter-state"
                        className="h-10 rounded-xl border border-slate-200 bg-white px-3 font-body text-sm text-[var(--text-primary)] focus:border-[var(--brand-orange)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                    >
                        <option value="">All sources</option>
                        {knownSlugs.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-1 flex-col gap-1">
                    <label htmlFor="wl-q" className="font-heading text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                        Search
                    </label>
                    <div className="relative">
                        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <Input
                            id="wl-q"
                            placeholder="Email or mobile…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="admin-waitlist-search"
                            className="pl-9 rounded-xl border-slate-200 focus:border-[var(--brand-orange)] focus-visible:ring-1 focus-visible:ring-[var(--brand-orange)]"
                        />
                    </div>
                </div>
                <div className="flex items-end gap-2 sm:pt-5">
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        data-testid="admin-waitlist-refresh-btn"
                        className="rounded-xl border-slate-200"
                    >
                        <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    <Button
                        onClick={onExport}
                        className="btn-primary"
                        data-testid="admin-waitlist-export-btn"
                    >
                        <Download size={14} className="mr-2" /> Export CSV
                    </Button>
                </div>
            </div>

            {/* Table */}
            <div className="card-soft !p-0 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse">
                        <thead>
                            <tr className="border-b border-[var(--border-soft)] bg-slate-50/70 text-left">
                                <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Email</th>
                                <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Mobile</th>
                                <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Source</th>
                                <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">City</th>
                                <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Signed up</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-16 text-center">
                                        <h2 className="font-heading text-lg font-bold text-[var(--text-primary)]">No waitlist entries yet</h2>
                                        <p className="mt-1 font-body text-sm text-[var(--text-muted)]">
                                            Try changing the source or search filter.
                                        </p>
                                    </td>
                                </tr>
                            )}
                            {items.map((row) => (
                                <tr
                                    key={row.id}
                                    className="border-b border-[var(--border-soft)] transition hover:bg-slate-50"
                                    data-testid={`admin-waitlist-row-${row.id}`}
                                >
                                    <td className="px-4 py-3 font-body text-sm text-[var(--text-primary)]">{row.email || "—"}</td>
                                    <td className="px-4 py-3 font-body text-sm text-[var(--text-primary)]">{row.mobile || "—"}</td>
                                    <td className="px-4 py-3">
                                        {row.state_slug ? (
                                            <span className="status-pill status-pill-neutral" data-testid={`row-source-${row.id}`}>{row.state_slug}</span>
                                        ) : (
                                            <span className="font-body text-xs text-[var(--text-muted)]">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-body text-sm text-[var(--text-primary)]">{row.city || "—"}</td>
                                    <td className="px-4 py-3 font-body text-sm text-[var(--text-muted)]">{formatDate(row.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Pagination footer */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-soft)] bg-white px-4 py-3">
                    <div className="font-body text-xs text-[var(--text-muted)]" data-testid="admin-waitlist-pagination-total">
                        {total} total · Page {page + 1} of {totalPages}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0 || loading}
                            data-testid="admin-waitlist-pagination-prev"
                            className="rounded-xl border-slate-200"
                        >
                            Prev
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1 || loading}
                            data-testid="admin-waitlist-pagination-next"
                            className="rounded-xl border-slate-200"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
